import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Users, ChevronRight, AlertCircle, Clock, Grid, List, Image as ImageIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { SEASON_EVENTS_USER_API, REGISTRATIONS_API, COMPANY_SETTINGS_API, fetchWithAuth } from '../../utils/api';
import ddLogo from '../../components/ui/dd.png';
import { SkeletonEventCard, SkeletonStatCard } from '../../components/ui/SkeletonLoader';
import RegistrationTestButton from '../../components/debug/RegistrationTestButton';
import { getCurrentUser } from '@aws-amplify/auth';
import * as registrationService from '../../services/registration/registrationService';

// Create a constant for the Dance Digital logo
const DANCE_DIGITAL_LOGO = ddLogo;

// Cache for events page data to prevent duplicate API calls
let eventsPageDataCache = null;
let eventsPageDataPromise = null;

const EventsPage = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  const [events, setEvents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [seasonEvents, setSeasonEvents] = useState({ seasons: [], events: [], eventTypes: [] });
  
  // Simple image error handler that replaces broken images with a data URL
  const handleImageError = (e) => {
    e.target.onerror = null; // prevent infinite error loops
    e.target.src = DANCE_DIGITAL_LOGO;
  };

  // Handle registration with proper initialization
  const handleRegistrationClick = async (event) => {
    try {
      console.log('Starting registration process for event:', event.id);
      
      // Get current user
      const user = await getCurrentUser();
      if (!user || !user.userId) {
        console.error('User not authenticated');
        // Navigate to login or show error
        return;
      }

      console.log('User authenticated:', user.userId);

      // Check for existing registration
      const existingReg = await registrationService.checkExistingRegistration(event.id, user.userId);
      
      if (existingReg) {
        console.log('Found existing registration:', existingReg.registrationId);
        // Navigate to existing registration
        window.location.href = `/user/registration/overview?eventId=${event.id}`;
        return;
      }

      console.log('No existing registration, creating new one...');
      
      // Create new registration
      const newRegistration = await registrationService.createNewRegistration(
        event.id,
        user.userId,
        {
          name: event.name,
          startDate: event.startDate,
          endDate: event.endDate,
          location: event.location,
          venue: event.venue
        }
      );

      if (newRegistration && newRegistration.registrationId) {
        console.log('Successfully created registration:', newRegistration.registrationId);
        // Navigate to registration flow
        window.location.href = `/user/registration/overview?eventId=${event.id}`;
      } else {
        console.error('Failed to create registration');
        // Show error to user
      }

    } catch (error) {
      console.error('Registration error:', error);
      // Show error to user
    }
  };

  // Replace the current getRegistrationProgress function
  const getRegistrationProgress = (eventId, registration) => {
    // If we have a real registration, extract step data from it
    if (registration) {
      // Check if registration is completed
      if (registration.status === 'completed' || 
          registration.currentStepPath === 'success' ||
          (registration.currentStepPath === 'payment' && registration.payment && registration.payment.status === 'completed')) {
        // Return null to indicate completed registration
        return null;
      }
      
      const currentStep = registration.currentStep || 0;
      const totalSteps = 8; // Default total steps
      
      // Map step paths to names
      const STEP_PATH_TO_NAME = {
        'overview': 'OVERVIEW',
        'routines': 'ROUTINES',
        'dancers': 'DANCERS',
        'teachers': 'TEACHERS',
        'observers': 'OBSERVERS',
        'review': 'REVIEW',
        'payment': 'PAYMENT',
        'success': 'SUCCESS'
      };
      
      // Use the currentStepPath to get the proper name
      const stepPath = registration.currentStepPath || 'overview';
      const stepName = STEP_PATH_TO_NAME[stepPath] || 'OVERVIEW';
      
      return {
        step: currentStep,
        totalSteps: totalSteps,
        stepName: stepName,
        stepPath: stepPath
      };
    }
    
    // Default for new registrations
    return { 
      step: 1, 
      totalSteps: 8, 
      stepName: "OVERVIEW", 
      stepPath: "overview" 
    };
  };

  // Optimized fetch with deduplication to prevent React Strict Mode duplicate calls
  useEffect(() => {
    let isCancelled = false;
    
    const fetchData = async () => {
      try {
        // 🚀 DEDUPLICATION: If there's already a promise, wait for it
        if (eventsPageDataPromise) {
          console.log("📊 Events fetch already in progress, waiting for existing promise...");
          const data = await eventsPageDataPromise;
          if (!isCancelled && data) {
            console.log("📊 Using data from existing events fetch");
            setSeasonEvents(data.seasonEventsData);
            setCompanySettings(data.settingsResponse);
            setEvents(data.allEvents);
            setRegistrations(data.userRegistrations);
            setLoading(false);
          }
          return;
        }

        // If we have cached data, use it
        if (eventsPageDataCache) {
          console.log("📊 Using cached events data, skipping API calls");
          setSeasonEvents(eventsPageDataCache.seasonEventsData);
          setCompanySettings(eventsPageDataCache.settingsResponse);
          setEvents(eventsPageDataCache.allEvents);
          setRegistrations(eventsPageDataCache.userRegistrations);
          setLoading(false);
          return;
        }

        console.log("📡 Fetching events page data...");
        setLoading(true);

        // Create the fetch promise
        eventsPageDataPromise = (async () => {
          // 1) Fetch all events using the user-specific endpoint, registrations, and company settings in parallel
          const [seasonEventsData, regResponse, settingsResponse] = await Promise.all([
            fetchWithAuth(SEASON_EVENTS_USER_API), // Use the new endpoint for users
            fetchWithAuth(REGISTRATIONS_API),
            fetchWithAuth(COMPANY_SETTINGS_API)
          ]);
          
          // Debug log raw data (only once now)
          console.log("Raw Season Events Data:", seasonEventsData);
          console.log("User Registrations:", regResponse);
          console.log("Company Settings:", settingsResponse);
          
          const userRegistrations = regResponse.registrations || [];
          
          // Process events from the seasons events API
          const allEvents = processEventsFromSeasonEvents(seasonEventsData, userRegistrations, settingsResponse);
          console.log("Processed Events:", allEvents);
          
          // Debug the filter categories
          if (allEvents.length > 0) {
            const incompleteRegs = allEvents.filter(event => event.registered && event.progress && !event.isComplete);
            const completedRegs = allEvents.filter(event => event.registered && 
              (event.isComplete || event.status === 'completed' || 
               event.currentStepPath === 'success' ||
               (event.currentStepPath === 'payment' && event.payment && event.payment.status === 'completed')));
            const availableEvts = allEvents.filter(event => !event.registered && event.registrationStatus?.toLowerCase() === "open");
            const upcomingEvts = allEvents.filter(event => !event.registered && event.registrationStatus?.toLowerCase() !== "open");
            
            console.log("Incomplete Registrations:", incompleteRegs.length, incompleteRegs);
            console.log("Completed Registrations:", completedRegs.length, completedRegs);
            console.log("Available Events:", availableEvts.length, availableEvts);
            console.log("Upcoming Events:", upcomingEvts.length, upcomingEvts);
          } else {
            console.warn("No events were processed from the season events data");
          }

          const data = {
            seasonEventsData,
            settingsResponse,
            allEvents,
            userRegistrations
          };

          // Cache the data
          eventsPageDataCache = data;
          
          console.log("✅ Events page data fetched successfully");
          return data;
        })();

        // Wait for the promise and update state
        const data = await eventsPageDataPromise;
        
        if (!isCancelled) {
          setSeasonEvents(data.seasonEventsData);
          setCompanySettings(data.settingsResponse);
          setEvents(data.allEvents);
          setRegistrations(data.userRegistrations);
        }
      } catch (err) {
        console.error("Error fetching events data:", err);
        if (!isCancelled) {
          setError(err.message || "Failed to fetch data. Please try again.");
        }
        // Clear cache on error
        eventsPageDataCache = null;
        eventsPageDataPromise = null;
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
        // Clear the promise after completion
        eventsPageDataPromise = null;
      }
    };

    fetchData();
    
    // Cleanup function to prevent state updates if component unmounts
    return () => {
      isCancelled = true;
    };
  }, []);

  // Process events from season events data
  const processEventsFromSeasonEvents = (seasonEventsData, userRegistrations, companySettings) => {
    // Extract arrays from the data, handling possible DynamoDB format
    let events = seasonEventsData.events || [];
    let seasons = seasonEventsData.seasons || [];
    let eventTypes = seasonEventsData.eventTypes || [];
    
    // Normalize data if it's in DynamoDB format
    if (events.length > 0 && events[0].M) {
      events = events.map(item => {
        const eventObj = {};
        Object.keys(item.M).forEach(key => {
          // Extract the value from DynamoDB format
          const dbValue = item.M[key];
          eventObj[key] = dbValue.S || dbValue.N || (dbValue.BOOL !== undefined ? dbValue.BOOL : dbValue);
        });
        return eventObj;
      });
    }
    
    if (seasons.length > 0 && seasons[0].M) {
      seasons = seasons.map(item => {
        const seasonObj = {};
        Object.keys(item.M).forEach(key => {
          const dbValue = item.M[key];
          seasonObj[key] = dbValue.S || dbValue.N || (dbValue.BOOL !== undefined ? dbValue.BOOL : dbValue);
        });
        return seasonObj;
      });
    }
    
    if (eventTypes.length > 0 && eventTypes[0].M) {
      eventTypes = eventTypes.map(item => {
        const typeObj = {};
        Object.keys(item.M).forEach(key => {
          const dbValue = item.M[key];
          typeObj[key] = dbValue.S || dbValue.N || (dbValue.BOOL !== undefined ? dbValue.BOOL : dbValue);
        });
        return typeObj;
      });
    }
    
    // Log the normalized data for debugging
    console.log("Normalized Events:", events);
    console.log("Normalized Seasons:", seasons);
    console.log("Normalized Event Types:", eventTypes);
    
    // Get the current date to determine if an event is open for registration
    const today = new Date();
    
    if (events.length === 0) {
      console.log("No events found in the data");
      return [];
    }
    
    return events.map(evt => {
      // Generate an ID based on name if none exists
      const eventId = evt.id || evt.name.replace(/\s+/g, '-').toLowerCase();
      
      // Find the matching registration if any
      const registration = userRegistrations.find(r => 
        r.eventId === eventId || 
        r.eventId === evt.name || 
        (r.eventName && r.eventName === evt.name)
      );
      const hasReg = !!registration;
      
      // Determine registration status based on dates and any explicit status
      // The backend lambda should provide this based on the organizer's settings
      let registrationStatus = evt.registrationStatus || "Closed"; // Default if not provided
      
      // Get progress if there's a registration
      const progress = hasReg ? getRegistrationProgress(eventId, registration) : null;
      
      // Determine logo URL with fallback
      const hasValidLogo = evt.logoUrl && evt.logoUrl.trim() !== '';
      
      // The backend now provides organizerName directly for each event
      const organizerName = evt.organizerName || companySettings?.companyName || "Unknown organizer";
      
      return {
        ...evt,
        id: eventId,
        eventId: eventId,
        registered: hasReg,
        registrationStatus: registrationStatus,
        // Set logoUrl to null for empty strings so the fallback will be used consistently
        logoUrl: hasValidLogo ? evt.logoUrl : null,
        // Add progress information if it exists
        progress: progress,
        // For completed registrations (explicit check for null progress)
        isComplete: hasReg && progress === null,
        // Use the organizerName provided by the backend lambda
        organizerName: organizerName,
        // Extract event type information if available
        eventTypeInfo: eventTypes.find(type => type.name === evt.eventType)
      };
    });
  };

  // Parse date string to Date object, handling MM-DD-YYYY format
  const parseDate = (dateString) => {
    if (!dateString) return new Date(0);
    
    // Handle MM-DD-YYYY format properly
    if (dateString.includes('-') && dateString.length === 10 && dateString.indexOf('-') === 2) {
      // MM-DD-YYYY format - convert to YYYY-MM-DD for reliable parsing
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const [month, day, year] = parts;
        return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
      }
    }
    
    return new Date(dateString);
  };

  // Format date range - numerical format to save space
  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return "N/A";
  
    const createLocalDate = (dateStr) => {
      const date = parseDate(dateStr);
      date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
      return date;
    };
  
    const start = createLocalDate(startDate);
    const end = createLocalDate(endDate);
    
    // Short format: MM/DD/YY
    const formatShort = (date) => {
      return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear().toString().substr(2, 2)}`;
    };
  
    // If same day
    if (start.toDateString() === end.toDateString()) {
      return formatShort(start);
    }
  
    // Different days
    return `${formatShort(start)} - ${formatShort(end)}`;
  };

  // Format single date - numerical format
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    const date = parseDate(dateString);
    date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
    
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear().toString().substr(2, 2)}`;
  };

  // Helper function to get event date for sorting
  const getEventDate = (event) => {
    if (!event.startDate) return new Date(0); // Default to epoch if no date
    return parseDate(event.startDate);
  };

  // Filter events by registration status and sort by date (nearest first)
  const incompleteRegistrations = events
    .filter(event => event.registered && event.progress && !event.isComplete)
    .sort((a, b) => getEventDate(a) - getEventDate(b));
    
  const completedRegistrations = events
    .filter(event => event.registered && 
      (event.isComplete || 
       event.status === 'completed' || 
       event.currentStepPath === 'success' ||
       (event.currentStepPath === 'payment' && event.payment && event.payment.status === 'completed')))
    .sort((a, b) => getEventDate(a) - getEventDate(b));
    
  const availableEvents = events
    .filter(event => !event.registered && event.registrationStatus?.toLowerCase() === "open")
    .sort((a, b) => getEventDate(a) - getEventDate(b));
    
  const upcomingEvents = events
    .filter(event => !event.registered && event.registrationStatus?.toLowerCase() !== "open")
    .sort((a, b) => getEventDate(a) - getEventDate(b));

  // Loading state
  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-6 pt-10 md:pt-0">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
        </div>
        
        {/* In Progress Section */}
        <div className={`rounded-xl overflow-hidden ${
          isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'bg-base-100'
        }`}>
          <div className="p-4">
            <SkeletonStatCard />
            <div className="space-y-3 mt-4">
              {[...Array(2)].map((_, index) => (
                <SkeletonEventCard key={`progress-${index}`} />
              ))}
            </div>
          </div>
        </div>
        
        {/* Available Events */}
        <div className={`rounded-xl overflow-hidden ${
          isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'bg-base-100'
        }`}>
          <div className="p-4">
            <SkeletonStatCard />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {[...Array(4)].map((_, index) => (
                <SkeletonEventCard key={`available-${index}`} />
              ))}
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pt-10 md:pt-0">
          <div
            className={`p-4 rounded-lg ${
              isDarkMode
                ? "bg-red-500/10 border border-red-500/20 text-red-400"
                : "alert alert-error shadow-lg"
            }`}
          >
            <div className="flex items-center">
              <AlertCircle className="h-6 w-6 mr-2" />
              <span>{error}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
      <div className="space-y-5 pt-10 md:pt-0">
      <div className="mb-5 md:flex justify-between items-center">
        <div>
          <h1 className={`text-2xl md:text-3xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>Events</h1>
          <p className={`text-sm md:text-base ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
            Register for dance conventions, manage your registrations, and track upcoming events.
          </p>
        </div>
        <button className="hidden md:block btn btn-primary mt-4 md:mt-0">
          View Calendar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Incomplete Registrations */}
      <div
        className={`card ${
          isDarkMode
              ? "bg-[#1a1f2d]/80 backdrop-blur-sm border-l-4 border-l-orange-500 border border-white/10"
              : "bg-base-100 border-l-4 border-l-orange-500"
          } shadow overflow-hidden`}
      >
        <div className="p-4">
          <h2
              className={`text-lg font-bold mb-3 ${
              isDarkMode ? "text-white" : "text-base-content"
            }`}
          >
              <div className="flex items-center">
                <svg 
                  viewBox="0 0 24 24" 
                  className="w-5 h-5 mr-2 text-orange-500" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                </svg>
                In Progress
              </div>
          </h2>
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-2">
              {incompleteRegistrations.length > 0 ? (
                incompleteRegistrations.map((event) => (
                <div
                  key={event.id}
                    className={`flex items-center p-2 rounded-lg transition-colors duration-200 ${
                    isDarkMode
                      ? "bg-gray-800/50 hover:bg-gray-800"
                      : "bg-base-200 hover:bg-base-300"
                  }`}
                >
                    <div className="flex-shrink-0 flex justify-center items-center w-12 h-12 mr-2">
                      <img 
                        src={event.logoUrl || DANCE_DIGITAL_LOGO} 
                        alt={`${event.name || 'Convention'} logo`}
                        className="max-h-10 max-w-full object-contain"
                        onError={handleImageError}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-base-content"}`}>
                            {event.name || event.city}
                          </h3>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                              isDarkMode
                                ? "bg-gray-700 text-gray-300"
                                : "bg-gray-200 text-gray-800"
                            }`}>
                              {formatDateRange(event.startDate, event.endDate)}
                            </span>
                            {event.earlyRateDeadline && (
                              <span className={`px-1.5 py-0.5 rounded-full text-2xs ${
                                isDarkMode
                                  ? "bg-yellow-900/30 text-yellow-300"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}>
                                <Clock className="h-2.5 w-2.5 inline mr-0.5" />
                                Early: {formatDate(event.earlyRateDeadline)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          <span className={`px-1 py-0.5 rounded-full text-2xs ${
                            isDarkMode
                              ? "bg-purple-900/30 text-purple-300"
                              : "bg-purple-100 text-purple-800"
                          }`}>
                            {event.organizerName || "Unknown"}
                          </span>
                          <span className={`px-1 py-0.5 rounded-full text-2xs ${
                            isDarkMode
                              ? "bg-blue-900/30 text-blue-300"
                              : "bg-blue-100 text-blue-800"
                          }`}>
                            {event.eventType || "Conv"}
                          </span>
                        </div>
                      </div>
                    
                      <div className="w-full mt-1">
                        <div className="w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-orange-400 to-orange-600 h-1.5 rounded-full" 
                            style={{ width: `${event.progress ? (event.progress.step / event.progress.totalSteps) * 100 : 0}%` }}>
                          </div>
                        </div>
                        <div className="flex justify-between mt-1 items-center">
                          <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>
                            <span className="text-orange-500 font-bold">{event.progress?.stepName || "OVERVIEW"}</span>
                          </span>
                          <Link 
                            to={`/user/registration/${event.progress?.stepPath || 'overview'}?eventId=${event.id}`}
                            className={`btn btn-xs btn-sm h-6 min-h-0 bg-orange-500 hover:bg-orange-600 text-white border-none text-xs`}
                          >
                            Continue
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div
                  className={`p-3 text-center rounded-lg ${
                    isDarkMode ? "bg-gray-800/50" : "bg-base-200"
                  }`}
                >
                  <p
                    className={
                      isDarkMode ? "text-gray-400" : "text-base-content/70"
                    }
                  >
                    No registrations in progress.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Completed Registrations */}
        <div
          className={`card ${
            isDarkMode
              ? "bg-[#1a1f2d]/80 backdrop-blur-sm border-l-4 border-l-teal-500 border border-white/10"
              : "bg-base-100 border-l-4 border-l-teal-500"
          } shadow overflow-hidden`}
        >
          <div className="p-4">
            <h2
              className={`text-lg font-bold mb-3 ${
                          isDarkMode ? "text-white" : "text-base-content"
                        }`}
                      >
              <div className="flex items-center">
                <svg 
                  viewBox="0 0 24 24" 
                  className="w-5 h-5 mr-2 text-teal-500" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Registered
              </div>
            </h2>
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-2">
              {completedRegistrations.length > 0 ? (
                completedRegistrations.map((event) => (
                  <div
                    key={event.id}
                    className={`flex items-center p-2 rounded-lg transition-colors duration-200 ${
                      isDarkMode
                        ? "bg-gray-800/50 hover:bg-gray-800"
                        : "bg-base-200 hover:bg-base-300"
                    }`}
                  >
                    <div className="flex-shrink-0 flex justify-center items-center w-12 h-12 mr-2">
                      <img 
                        src={event.logoUrl || DANCE_DIGITAL_LOGO} 
                        alt={`${event.name || 'Convention'} logo`}
                        className="max-h-10 max-w-full object-contain"
                        onError={handleImageError}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-base-content"}`}>
                            {event.name || event.city}
                          </h3>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                              isDarkMode
                                ? "bg-gray-700 text-gray-300"
                                : "bg-gray-200 text-gray-800"
                            }`}>
                              {formatDateRange(event.startDate, event.endDate)}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          <span className={`px-1 py-0.5 rounded-full text-2xs ${
                            isDarkMode
                              ? "bg-purple-900/30 text-purple-300"
                              : "bg-purple-100 text-purple-800"
                          }`}>
                            {event.organizerName || "Unknown"}
                          </span>
                          <span className={`px-1 py-0.5 rounded-full text-2xs ${
                            isDarkMode
                              ? "bg-blue-900/30 text-blue-300"
                              : "bg-blue-100 text-blue-800"
                          }`}>
                            {event.eventType || "Conv"}
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-between mt-1">
                        <div className="flex items-center gap-1">
                          <span
                            className={`px-1.5 py-0.5 text-2xs font-medium rounded-full ${
                              isDarkMode
                                ? "bg-teal-500/10 text-teal-400"
                                : "bg-teal-100 text-teal-800"
                            }`}
                          >
                            {event.routinesRegistered || 0} Routines
                          </span>
                          <span
                            className={`px-1.5 py-0.5 text-2xs font-medium rounded-full ${
                              isDarkMode
                                ? "bg-indigo-500/10 text-indigo-400"
                                : "bg-indigo-100 text-indigo-800"
                            }`}
                          >
                            {event.dancersRegistered || 0} Dancers
                          </span>
                        </div>
                        <Link 
                          to={`/user/events/${event.id}/details`}
                          className={`btn btn-xs h-6 min-h-0 bg-teal-500 hover:bg-teal-600 text-white border-none`}
                        >
                          Details
                        </Link>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div
                  className={`p-3 text-center rounded-lg ${
                    isDarkMode ? "bg-gray-800/50" : "bg-base-200"
                  }`}
                >
                  <p
                    className={
                      isDarkMode ? "text-gray-400" : "text-base-content/70"
                    }
                  >
                    You don't have any registered events.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Available Events */}
      <div
        className={`card ${
          isDarkMode
            ? "bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/10"
            : "bg-base-100"
        } shadow`}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
          <h2
              className={`text-xl font-bold flex items-center ${
              isDarkMode ? "text-white" : "text-base-content"
            }`}
          >
              <svg 
                viewBox="0 0 24 24" 
                className="w-6 h-6 mr-2 text-primary" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M3 10H21" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Upcoming Events
          </h2>
            
            <div className="flex gap-2">
              <button 
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' 
                  ? isDarkMode 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-primary text-white' 
                  : isDarkMode 
                    ? 'bg-gray-800 text-gray-300' 
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                <Grid size={16} />
              </button>
              <button 
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' 
                  ? isDarkMode 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-primary text-white' 
                  : isDarkMode 
                    ? 'bg-gray-800 text-gray-300' 
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                <List size={16} />
              </button>
            </div>
          </div>
          
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[500px] overflow-y-auto pr-2">
              {[...availableEvents, ...upcomingEvents].length > 0 ? (
                [...availableEvents, ...upcomingEvents].map((event) => (
                  <div
                    key={event.id}
                    className={`flex p-3 rounded-lg transition-colors duration-200 ${
                      isDarkMode
                        ? "bg-gray-800/50 hover:bg-gray-800"
                        : "bg-base-200 hover:bg-base-300"
                    }`}
                  >
                    <div className="flex-shrink-0 flex justify-center items-center w-14 h-14 mr-3">
                      <img 
                        src={event.logoUrl || DANCE_DIGITAL_LOGO} 
                        alt={`${event.name || 'Convention'} logo`}
                        className="max-h-12 max-w-full object-contain"
                        onError={handleImageError}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className={`font-medium text-base ${isDarkMode ? "text-white" : "text-base-content"}`}>
                            {event.name || event.city}
                          </h3>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              isDarkMode
                                ? "bg-gray-700 text-gray-300"
                                : "bg-gray-200 text-gray-800"
                            }`}>
                              {formatDateRange(event.startDate, event.endDate)}
                            </span>
                            {event.earlyRateDeadline && (
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                isDarkMode
                                  ? "bg-yellow-900/30 text-yellow-300"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}>
                                <Clock className="h-3 w-3 inline mr-1" />
                                Early: {formatDate(event.earlyRateDeadline)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                            isDarkMode
                              ? "bg-purple-900/30 text-purple-300"
                              : "bg-purple-100 text-purple-800"
                          }`}>
                            {event.organizerName || "Unknown"}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                            isDarkMode
                              ? "bg-blue-900/30 text-blue-300"
                              : "bg-blue-100 text-blue-800"
                          }`}>
                            {event.eventType || "Conv"}
                          </span>
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${
                              event.registrationStatus?.toLowerCase() === "open"
                                ? isDarkMode
                                  ? "bg-blue-500/10 text-blue-400"
                                  : "bg-blue-100 text-blue-800"
                                : isDarkMode
                                ? "bg-purple-500/10 text-purple-400"
                                : "bg-purple-100 text-purple-800"
                          }`}
                          >
                            {event.registrationStatus || "Unknown"}
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-end mt-2 gap-2">
                        {event.registrationStatus?.toLowerCase() === "open" ? (
                          <>
                            <button
                              onClick={() => handleRegistrationClick(event)}
                              className={`btn btn-xs ${isDarkMode ? "btn-primary" : "btn-primary"}`}
                            >
                              Register
                            </button>
                            {/* Add debug test button for the first open event */}
                            {availableEvents.findIndex(e => e.id === event.id) === 0 && (
                              <RegistrationTestButton event={event} />
                            )}
                          </>
                        ) : (
                          <button
                            className={`btn btn-xs ${isDarkMode ? "btn-disabled" : "btn-disabled"}`}
                            disabled
                          >
                            Soon
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div
                  className={`p-4 text-center rounded-lg col-span-2 ${
                    isDarkMode ? "bg-gray-800/50" : "bg-base-200"
                  }`}
                >
                  <p
                    className={
                      isDarkMode ? "text-gray-400" : "text-base-content/70"
                    }
                  >
                    No events available at this time. Check back soon!
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
              {[...availableEvents, ...upcomingEvents].length > 0 ? (
                [...availableEvents, ...upcomingEvents].map((event) => (
                  <div
                    key={event.id}
                    className={`flex items-center p-2 rounded-lg transition-colors duration-200 ${
                      isDarkMode
                        ? "bg-gray-800/50 hover:bg-gray-800"
                        : "bg-base-200 hover:bg-base-300"
                    }`}
                  >
                    <div className="flex-shrink-0 flex justify-center items-center h-12 w-12 mr-2">
                      <img 
                        src={event.logoUrl || DANCE_DIGITAL_LOGO} 
                        alt={`${event.name || 'Convention'} logo`}
                        className="max-h-10 max-w-full object-contain"
                        onError={handleImageError}
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <h3 className={`font-medium text-sm truncate mr-2 ${
                          isDarkMode ? "text-white" : "text-base-content"
                        }`}>
                          {event.name || event.city}
                        </h3>
                        <span className={`px-1.5 py-0.5 text-2xs font-medium rounded-full ${
                          event.registrationStatus?.toLowerCase() === "open"
                            ? isDarkMode
                              ? "bg-blue-500/10 text-blue-400"
                              : "bg-blue-100 text-blue-800"
                            : isDarkMode
                            ? "bg-purple-500/10 text-purple-400"
                            : "bg-purple-100 text-purple-800"
                        }`}>
                          {event.registrationStatus || "Unknown"}
                        </span>
                      </div>
                      
                      <div className="flex items-center text-xs">
                        <span className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                          {formatDateRange(event.startDate, event.endDate)}
                        </span>
                        <span className="mx-1 text-gray-400">•</span>
                        <span className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                          {event.organizerName || "Unknown"}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center mt-0.5">
                        {event.earlyRateDeadline && (
                          <span className={`text-2xs flex items-center ${
                            isDarkMode ? "text-yellow-300" : "text-yellow-600"
                          }`}>
                            <Clock className="h-2.5 w-2.5 inline mr-0.5" />
                            Early: {formatDate(event.earlyRateDeadline)}
                          </span>
                        )}
                        
                        {event.registrationStatus?.toLowerCase() === "open" ? (
                          <button
                            onClick={() => handleRegistrationClick(event)}
                            className={`btn btn-xs ${isDarkMode ? "btn-primary" : "btn-primary"}`}
                          >
                            Register
                          </button>
                        ) : (
                          <button
                            className={`btn btn-xs ${isDarkMode ? "btn-disabled" : "btn-disabled"}`}
                            disabled
                          >
                            Soon
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div
                  className={`p-4 text-center rounded-lg ${
                    isDarkMode ? "bg-gray-800/50" : "bg-base-200"
                  }`}
                >
                  <p
                    className={
                      isDarkMode ? "text-gray-400" : "text-base-content/70"
                    }
                  >
                    No events available at this time. Check back soon!
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default EventsPage;